import { connect } from 'react-redux';
import styled from 'styled-components';
import { NumericFormatProps } from 'react-number-format';
import { useMediaQuery } from 'react-responsive';
import PolicyItem from './PolicyItem';
import { SummaryInputErrorMsg, SummaryNumberInput } from './styles';
import MultiRow from '../../Shared/MultiRow';
import LoadingSpinnerDots from '../../Shared/LoadingSpinnerDots';
import Spacer from '../../Shared/Spacer';
import {
  POLICY_OPTIONS,
  PROPERTY_LABELS,
  TOOL_TIP_CONTENT_KEY,
  VALUE_OPTIONS,
  POLICY_ARIA_LABELS,
  PROPERTY_LABEL_SUBTITLES,
} from '../../../constants';
import { formatCurrency } from '../../../helpers/formatNumber';
import lightbulbIcon from '../../../icons/lightbulb.svg';
import { selectLiquorLiability, selectTwoFourLimitsAvailable } from '../../../selectors/application';
import { selectQuotes } from '../../../selectors/quote';
import RetroactiveDate from '../../Shared/RetroactiveDate';
import { useFeatureFlags } from '../../../toggle_tools/featureFlagTools';

const PolicySummary = ({
  policyType,
  policyValue,
  inputValue,
  selectedPolicies = [],
  expanded,
  setExpanded,
  updateValue,
  inputType,
  isDisabled,
  errorMessage,
  isReadOnly = false,
  isFlorida = false,
  hasLiquorLiability,
  twoFourLimitsAvailable,
  quotes,
  hasYearsOfPriorActsError,
  options,
  isLoadingOptions,
  description,
  coastalMessage,
}: {
  policyType: string;
  policyValue: number;
  inputValue: number;
  selectedPolicies: string[];
  expanded: string;
  setExpanded: (...args: any[]) => any;
  updateValue: (option: NumericFormatProps) => void;
  inputType: string;
  isDisabled: boolean;
  errorMessage?: { message: string };
  isReadOnly?: boolean;
  isFlorida?: boolean;
  hasLiquorLiability: boolean;
  twoFourLimitsAvailable?: boolean;
  quotes: Quote[];
  hasYearsOfPriorActsError: boolean;
  options?: number[];
  isLoadingOptions?: boolean;
  description?: React.ReactNode;
  coastalMessage?: string;
}) => {
  const isMobile = useMediaQuery({ maxWidth: 850 });
  const { feWindHailDeductibleFromApi } = useFeatureFlags();

  const BOPselected = selectedPolicies.includes('BOP');
  const GLselected = selectedPolicies.includes('GL');
  const PLselected = selectedPolicies.includes('PL');

  const incomeLimit = quotes?.[0]?.businessIncomeLimit;

  const shouldRender = () => {
    switch (policyType) {
      case POLICY_OPTIONS.BUSINESS_PERSONAL_PROPERTY_LIMIT:
      case POLICY_OPTIONS.BUILDING_LIMIT:
      case POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE:
      case POLICY_OPTIONS.GENERAL_LIABILITY_LIMIT:
      case POLICY_OPTIONS.PROPERTY_DAMAGE_DEDUCTIBLE:
        return BOPselected || GLselected;
      case POLICY_OPTIONS.WIND_HAIL_DEDUCTIBLE:
        return BOPselected && isFlorida;
      case POLICY_OPTIONS.PROFESSIONAL_LIABILITY_LIMIT:
      case POLICY_OPTIONS.DEDUCTIBLE:
      case POLICY_OPTIONS.RETROACTIVE_DATE:
        return PLselected;
      case POLICY_OPTIONS.BUSINESS_INCOME_LIMIT:
        return BOPselected && incomeLimit;
      default:
        return false;
    }
  };

  const RenderErrorMessage = () =>
    errorMessage ? <SummaryInputErrorMsg>{errorMessage.message}</SummaryInputErrorMsg> : <Spacer height={32} />;

  const valueOptions = (policyType: string) => {
    if (policyType === POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE && options?.length) {
      return options;
    }
    if (policyType === POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE && isFlorida) {
      return VALUE_OPTIONS['PROPERTY_CLAIMS_DEDUCTIBLE_FL'];
    }
    return VALUE_OPTIONS[policyType];
  };

  const policyTypeIsGLLimit = policyType === POLICY_OPTIONS.GENERAL_LIABILITY_LIMIT;
  const isTwoFourLimitDisabled = !twoFourLimitsAvailable || hasLiquorLiability;

  const disabledOptions = (policyType: string) => {
    if (policyType === POLICY_OPTIONS.GENERAL_LIABILITY_LIMIT && isTwoFourLimitDisabled) {
      return [2_000_000];
    }
    return [];
  };

  let twoFourUnavailableText;
  if (policyTypeIsGLLimit) {
    if (!twoFourLimitsAvailable) {
      twoFourUnavailableText = isMobile
        ? '$2M limit is unavailable for selected industry and location'
        : 'Unavailable for selected industry and location';
    } else if (hasLiquorLiability) {
      twoFourUnavailableText = isMobile
        ? '$2M limit is unavailable when Liquor Liability is added'
        : 'Unavailable with Liquor Liability';
    }
  }
  return shouldRender() ? (
    <PolicyItem
      policyType={policyType}
      propertyName={PROPERTY_LABELS[policyType]}
      propertyValue={policyValue}
      isReadOnly={isReadOnly}
      toolTipContentKey={inputType !== 'component' ? TOOL_TIP_CONTENT_KEY[policyType] : ''}
      expanded={
        expanded === PROPERTY_LABELS[policyType] ||
        (hasYearsOfPriorActsError && policyType === POLICY_OPTIONS.RETROACTIVE_DATE)
      }
      setExpanded={setExpanded}
      testId={policyType}
      labelSubtitle={PROPERTY_LABEL_SUBTITLES[policyType]}
      isFlorida={isFlorida}
      description={description}
      coastalMessage={coastalMessage}
    >
      {(() => {
        if (isReadOnly) return <></>;
        if (inputType === 'component') {
          /* Fix: Added the RetroactiveDate component as part of a business requirement to 
          show the Retroactive Date field in the Summary page */
          if (policyType === POLICY_OPTIONS.RETROACTIVE_DATE) {
            return <RetroactiveDate updateValue={updateValue} />;
          }
        }
        if (inputType === 'text') {
          return (
            <>
              <SummaryNumberInput
                value={inputValue != null ? inputValue : policyValue}
                onValueChange={updateValue}
                customwidth="quarter"
                marginbottom="none"
                thousandSeparator={true}
                prefix="$"
                aria-label={POLICY_ARIA_LABELS[policyType]}
                data-cy={POLICY_ARIA_LABELS[policyType]}
                disabled={isDisabled}
                hasError={Boolean(errorMessage)}
              />
              <RenderErrorMessage />
            </>
          );
        }
        if (isLoadingOptions) {
          return <LoadingSpinnerDots height={40} width={60} />;
        }

        const resolvedOptions = options || valueOptions(policyType);

        if (options && options.length === 1) {
          if (feWindHailDeductibleFromApi) return null;
          return (
            <>
              <CoastalHelperText>
                <img src={lightbulbIcon} alt="" width={14} height={14} />
                Only a {formatCurrency(options[0])} deductible is available due to the proximity to the coastline
              </CoastalHelperText>
              <SingleOptionButton type="button" aria-pressed="true">
                {formatCurrency(options[0])}
              </SingleOptionButton>
            </>
          );
        }

        if (options && options.length === 0) {
          return null;
        }

        return (
          <>
            <MultiRowWrapper narrow={options?.length === 2}>
              <MultiRow
                propertyName={PROPERTY_LABELS[policyType]}
                money={true}
                options={resolvedOptions}
                disabledOptions={disabledOptions(policyType)}
                value={policyValue}
                updateValue={updateValue}
                label={POLICY_ARIA_LABELS[policyType]}
                summary={true}
              />
            </MultiRowWrapper>
            {policyTypeIsGLLimit && twoFourUnavailableText && <Message>{twoFourUnavailableText}</Message>}
          </>
        );
      })()}
    </PolicyItem>
  ) : null;
};

const SingleLimitValue = styled.span`
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 16px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.tertiary.zero};
  display: block;
  padding: 0 0 10px;

  @media (max-width: 767px) {
    font-size: 19px;
    text-align: center;
  }
`;

const MultiRowWrapper = styled.div<{ narrow?: boolean }>`
  max-width: ${({ narrow }) => (narrow ? '300px' : 'none')};
  margin-left: 10px;

  @media (max-width: 850px) {
    margin: 0 auto;
  }

  &,
  & div {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

const Message = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #626266;
  align-self: flex-end;
  text-align: center;
  padding: 0 25px 0 0 !important;
  margin: -8px 0 16px;
  max-width: 136px;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  @media (max-width: 850px) {
    min-width: 100%;
    padding: 0 !important;
    margin: -12px 0 16px;
  }
`;

const SingleOptionButton = styled.button`
  width: 164px !important;
  height: 48px;
  border-radius: 24px;
  border: none;
  background-color: ${({ theme }) => theme.selectors.dynamicSelectors.backgroundSelected};
  color: ${({ theme }) => theme.selectors.dynamicSelectors.textColorSelected};
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 16px;
  font-weight: 600;
  cursor: default;
  padding: 0 !important;
  margin: 0 0 16px 10px;
`;

const CoastalHelperText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  padding: 0px 10px 10px !important;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.tertiary.zero};
`;

const mapStateToProps = (state: ReduxState) => ({
  hasLiquorLiability: Boolean(selectLiquorLiability(state)),
  twoFourLimitsAvailable: selectTwoFourLimitsAvailable(state),
  quotes: selectQuotes(state),
});

export default connect(mapStateToProps)(PolicySummary);
